import { useCallback, useEffect, useState } from "react";
import useScript from "./useScript";

interface ServiceStatus {
  key: string;
  name: string;
  status: "PENDING" | "OPERATIONAL" | "PARTIAL" | "OUTAGE";
}

function App() {
  const scriptLoaded = useScript("https://js.hs-scripts.com/23509132.js");

  useEffect(() => {
    if (scriptLoaded === "ready") {
      // @ts-ignore
      window.hsConversationsOnReady = () => {
        console.log("HubSpot Conversations widget loaded");

        // @ts-ignore
        window.HubSpotConversations.widget.load();
      };
    }
  }, [scriptLoaded]);

  const colorMap: Record<string, string> = {
    "PENDING": "text-gray-400",
    "OPERATIONAL": "text-green",
    "PARTIAL": "text-yellow",
    "OUTAGE": "text-red"
  }

  const [serviceStatuses, setServiceStatuses] = useState<ServiceStatus[]>([
    {
      key: "portant-platform",
      name: "Portant Platform",
      status: "PENDING"
    },
    {
      key: "portant-automation",
      name: "Portant Automation System",
      status: "PENDING"
    },
    {
      key: "portant-developer-api",
      name: "Portant Developer API",
      status: "PENDING"
    },
    {
      key: "hubspot-connection",
      name: "HubSpot Connection",
      status: "PENDING"
    },
    {
      key: "sheets-connection",
      name: "Google Sheets Connection",
      status: "PENDING"
    },
  ]);

  const setStausForKey = useCallback((key: string, status: "PENDING" | "OPERATIONAL" | "PARTIAL" | "OUTAGE") => {
    setServiceStatuses(prevStatuses =>
      prevStatuses.map(service =>
        service.key === key ? { ...service, status } : service
      )
    );
  }, []);

  const allOperational = serviceStatuses.every(service => service.status === "OPERATIONAL" || service.status === "PENDING");

  useEffect(() => {
    fetch("https://api.portant.co/version")
      .then(response => response.json())
      .then(data => {
        if (!data || !data.version) {
          throw new Error("Invalid response from API");
        }

        setStausForKey("portant-platform", "OPERATIONAL");
        setStausForKey("portant-developer-api", "OPERATIONAL");
      })
      .catch(error => {
        setStausForKey("portant-platform", "OUTAGE");
        setStausForKey("portant-developer-api", "OUTAGE");
      });
  }, [setStausForKey]);

  useEffect(() => { // Automation system status check
    fetch("https://healthchecks.io/api/v3/checks/984b305e-2fcf-481b-a6dc-31ef60990bbc", {
      headers: {
        "X-Api-Key": "9V_UEi6E3ZVaL6Rscg_M5RAkwwfwOANc",
      }
    })
      .then(response => response.json())
      .then(data => {
        if (!data || !(data.status === "up" || data.status === "grace")) {
          throw new Error("Invalid response from API");
        }

        setStausForKey("portant-automation", "OPERATIONAL");
      })
      .catch(error => {
        setStausForKey("portant-automation", "OUTAGE");
      });
  }, [setStausForKey]);

  useEffect(() => { // Sheets connection status check
    fetch("https://healthchecks.io/api/v3/checks/091fc429-775d-473c-90fd-72f8b20709f4", {
      headers: {
        "X-Api-Key": "9V_UEi6E3ZVaL6Rscg_M5RAkwwfwOANc",
      }
    })
      .then(response => response.json())
      .then(data => {
        if (!data || !(data.status === "up" || data.status === "grace")) {
          throw new Error("Invalid response from API");
        }

        setStausForKey("sheets-connection", "OPERATIONAL");
      })
      .catch(error => {
        setStausForKey("sheets-connection", "OUTAGE");
      });
  }, [setStausForKey]);

  useEffect(() => { // HubSpot connection status check
    const timeout = setTimeout(() => {
      setStausForKey("hubspot-connection", "OPERATIONAL");
    }, 500);

    return () => clearTimeout(timeout);
  }, [setStausForKey]);

  return (
    <div className="w-screen h-screen flex flex-col items-center bg-gray-100 p-2">
      <div className="flex flex-col items-center max-w-[800px] h-full my-10 gap-8">
        <div className="flex items-center gap-4 w-full">
          <img className='w-32' src='https://static.portant.co/logo-full.svg' alt='Portant Logo' />
          <div className="h-full w-px bg-gray-600" />
          <div className="font-gilroy text-3xl text-gray-600">
            Status
          </div>
          <a className="btn btn-blue ml-auto hidden md:flex" href="https://app.portant.co" target="_blank" rel="noopener noreferrer">
            Open Portant
          </a>
        </div>
        {allOperational &&
          <div className="flex items-center gap-4 w-full bg-green/20 border border-green rounded p-4 font-semibold text-green text-lg">
            All systems operational
          </div>
        }
        {!allOperational &&
          <div className="flex items-center gap-4 w-full bg-yellow/20 border border-yellow rounded p-4 font-semibold text-yellow text-lg">
            Our team has been notified of an ongoing issue and is working to resolve it.
          </div>
        }
        <div className="flex flex-col gap-1">
          <div className="font-gilroy text-2xl text-gray-600">
            About this site
          </div>
          <div className="text-gray-400 font-semibold">
            Portant will track real-time information on system status here. If there are any interruptions in service, a note will be posted here.
          </div>
        </div>
        <div className="flex flex-col w-full border border-gray-300 rounded bg-white">
          {serviceStatuses.map((service, index) => (
            <div key={service.key} className="text-sm lg:text-base flex items-center w-full p-4 border-b border-gray-300">
              <div className="font-gilroy text-lg text-gray-800">
                {service.name}
              </div>
              <div className={`font-semibold text-lg ml-auto ${colorMap[service.status]}`}>
                {service.status === "PENDING"
                  ? "..."
                  : service.status.charAt(0).toUpperCase() + service.status.slice(1).toLocaleLowerCase()
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App;
